import React, { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import ClearPhaseStartConfirm from './ClearPhaseStartConfirm';
import { formatInstant } from '../../lib/calendar-date';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { useAuth } from '../../contexts/AuthContext';
import { canClearPhaseStart } from '../../lib/roles';

/** "Oct 10 09:42" — the day and a 24-hour clock, in the tenant's zone. */
const START_FORMAT: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
};

/**
 * One line under a date on the job edit form, shown while that phase is
 * STARTED AND NOT ENDED: "Run started Oct 10 09:42 — moving the date won't move it."
 *
 * The board draws a started phase on its START day, not its scheduled one
 * (lib/dispatch-phase-cards.ts), so re-dating a started run here does not move
 * its card. TNT J-4372 is that case. The office needs to read it beside the
 * date they are about to change, not discover it on the board.
 *
 * Only a RECORDED start (`wash_started_at` / `run_started_at`). A pre-0403 run
 * carried over from `started_at` is not one, and the API's clear refuses it,
 * so offering Clear there would name a remedy that 409s.
 *
 * "Clear start" is for admin / office_staff / dispatcher (`canClearPhaseStart`)
 * and asks first: it rewrites what the field recorded.
 *
 * The open-phase test runs BEFORE any hook, in this wrapper, so a job with no
 * open start renders nothing and asks nothing of the auth or zone contexts.
 */
interface Props {
  jobId: string;
  jobNumber?: string;
  phase: 'wash' | 'run';
  startedAt: string | null | undefined;
  endedAt: string | null | undefined;
}

export default function PhaseStartNotice(props: Props) {
  if (props.startedAt == null || props.endedAt != null) return null;
  return <OpenPhaseStartLine {...props} startedAt={props.startedAt} />;
}

function OpenPhaseStartLine({ jobId, jobNumber, phase, startedAt }: Props & { startedAt: string }) {
  const timeZone = useTenantTimeZone();
  const { user } = useAuth();
  const canClear = canClearPhaseStart(user?.role);
  const [confirming, setConfirming] = useState(false);

  const label = phase === 'wash' ? 'Wash' : 'Run';
  const when = formatInstant(startedAt, timeZone, START_FORMAT) ?? '—';

  return (
    <div data-testid={`phase-start-notice-${phase}`} className="mt-1 flex items-center gap-2 text-xs text-fieldiq-text">
      <AlertTriangle size={12} className="flex-shrink-0 text-fieldiq-accent-signal" aria-hidden="true" />
      <span className="min-w-0 truncate">
        {label} started {when} — moving the date won't move it.
      </span>
      {canClear && (
        <button
          type="button"
          onClick={() => setConfirming(true)}
          className="flex-shrink-0 underline underline-offset-2 text-fieldiq-text hover:text-fieldiq-accent"
        >
          Clear start
        </button>
      )}
      {confirming && (
        <ClearPhaseStartConfirm
          jobId={jobId}
          jobNumber={jobNumber}
          phase={phase}
          startedLabel={when}
          onClose={() => setConfirming(false)}
        />
      )}
    </div>
  );
}
